import { useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Printer, ScanLine, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BarcodeScanner } from "@/components/colis/BarcodeScanner";
import { OPTIONS_DEPOT } from "@/lib/barcode";
import { api, errMsg } from "@/lib/api";
import { lireCode } from "@/lib/colisCode";
import { EtagereVisuel } from "./EtagereVisuel";
import { imprimerEtiquettes } from "./GestionChariots";

const ZONE = { params: { zone: "cage" } };
const codeAuto = (lettre, l, c) => `CG-${(lettre || "X").toUpperCase()}-${l}-${c}`;
const cle = (l, c) => `${l}-${c}`;

function FormEtagere({ etagere, onClose, onSaved }) {
  const [lettre, setLettre] = useState(etagere?.lettre || "");
  const [lignes, setLignes] = useState(etagere?.lignes || 3);
  const [colonnes, setColonnes] = useState(etagere?.colonnes || 4);
  const [codes, setCodes] = useState(() => Object.fromEntries((etagere?.emplacements || []).map((e) => [cle(e.ligne, e.colonne), e.code])));
  const [scanPour, setScanPour] = useState(null); // case dont on scanne l'étiquette (caméra)
  const [busy, setBusy] = useState(false);

  // Code proposé d'après la lettre et la position (modifiable, ou scanné si
  // les places ont déjà une étiquette : touchez la case puis scannez).
  const val = (l, c) => codes[cle(l, c)] ?? codeAuto(lettre, l, c);
  const setCode = (l, c, v) => setCodes((x) => ({ ...x, [cle(l, c)]: v.toUpperCase().replace(/\s/g, "") }));
  const borne = (v, max) => Math.max(1, Math.min(max, parseInt(v, 10) || 1));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const emplacements = [];
    for (let l = 1; l <= lignes; l += 1) for (let c = 1; c <= colonnes; c += 1) emplacements.push({ ligne: l, colonne: c, code: val(l, c) });
    const body = { type: "etagere", lettre, lignes, colonnes, emplacements };
    try {
      if (etagere) await api.put(`/depot/chariots/${etagere.id}`, body);
      else await api.post("/depot/chariots", body, ZONE);
      toast.success(`Étagère ${lettre.toUpperCase()} ${etagere ? "modifiée" : "créée"}`);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="space-y-4 rounded-xl border border-border p-4" data-testid="form-etagere">
      <div className="flex items-center justify-between">
        <p className="font-semibold">{etagere ? `Modifier l'étagère ${etagere.lettre}` : "Nouvelle étagère"}</p>
        <button type="button" onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground" aria-label="Fermer"><X size={16} /></button>
      </div>
      <div className="flex flex-wrap gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="et-lettre">Lettre *</Label>
          <Input id="et-lettre" value={lettre} onChange={(e) => setLettre(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 3))}
            placeholder="A" className="w-20 text-lg font-bold font-mono" required data-testid="et-lettre" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="et-lignes">Rangées</Label>
          <Input id="et-lignes" type="number" min={1} max={12} value={lignes} onChange={(e) => setLignes(borne(e.target.value, 12))} className="w-20" data-testid="et-lignes" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="et-colonnes">Places par rangée</Label>
          <Input id="et-colonnes" type="number" min={1} max={12} value={colonnes} onChange={(e) => setColonnes(borne(e.target.value, 12))} className="w-20" data-testid="et-colonnes" />
        </div>
      </div>
      <div>
        <Label>Code-barres de chaque place</Label>
        <p className="text-xs text-muted-foreground mb-2">Proposés d'après la lettre ; pour une étiquette déjà collée, touchez la case puis scannez-la (ou bouton caméra).</p>
        <div className="overflow-x-auto">
          <table className="border-collapse">
            <tbody>
              {Array.from({ length: lignes }, (_, i) => (
                <tr key={i}>
                  {Array.from({ length: colonnes }, (_, j) => {
                    const l = i + 1;
                    const c = j + 1;
                    return (
                      <td key={j} className="border border-border p-1 align-top">
                        <p className="text-[10px] text-muted-foreground">R{l} · P{c}</p>
                        <div className="flex gap-0.5">
                          <input value={val(l, c)} onChange={(e) => setCode(l, c, e.target.value)}
                            className="w-28 h-8 px-1.5 rounded border border-input bg-transparent font-mono text-xs" data-testid={`et-code-${l}-${c}`} />
                          <button type="button" onClick={() => setScanPour([l, c])} className="px-1 text-muted-foreground hover:text-foreground" title="Scanner l'étiquette à la caméra"><ScanLine size={13} /></button>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {scanPour && (
        <div className="space-y-2">
          <p className="text-sm">Scannez l'étiquette de la rangée {scanPour[0]}, place {scanPour[1]} :</p>
          <BarcodeScanner options={OPTIONS_DEPOT} accepter={(t) => !lireCode(t).type} aide="Visez l'étiquette de la place"
            onDetected={(r) => { setCode(scanPour[0], scanPour[1], r.text); setScanPour(null); }} onCancel={() => setScanPour(null)} />
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>Annuler</Button>
        <Button type="submit" disabled={!lettre || busy} data-testid="et-enregistrer">{busy && <Loader2 size={14} className="animate-spin" />} {etagere ? "Enregistrer" : "Créer l'étagère"}</Button>
      </div>
    </form>
  );
}

/** « Gérer les étagères » de la cage (permission « Gérer les étagères »). */
export function GestionEtageres({ open, onClose, onChange }) {
  const [etageres, setEtageres] = useState([]);
  const [form, setForm] = useState(null); // { etagere | null }
  const [confirmDel, setConfirmDel] = useState(null);

  const charger = async () => {
    try { setEtageres((await api.get("/depot/chariots", ZONE)).data.chariots); } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { if (open) { charger(); setForm(null); setConfirmDel(null); } }, [open]);

  const supprimer = async (et) => {
    try { await api.delete(`/depot/chariots/${et.id}`); toast.success(`Étagère ${et.lettre} supprimée`); setConfirmDel(null); charger(); onChange?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[92dvh] overflow-y-auto" data-testid="gestion-etageres">
        <DialogHeader>
          <DialogTitle>Gérer les étagères de la cage</DialogTitle>
          <DialogDescription>Une étagère = une lettre et une grille (rangées × places) ; chaque place a son code-barres.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setForm({ etagere: null })} data-testid="btn-nouvelle-etagere"><Plus size={14} /> Nouvelle étagère</Button>
          {etageres.length > 0 && <Button size="sm" variant="outline" onClick={() => imprimerEtiquettes(etageres)} data-testid="btn-etiquettes-cage"><Printer size={14} /> Imprimer les étiquettes</Button>}
        </div>
        {form && <FormEtagere etagere={form.etagere} onClose={() => setForm(null)} onSaved={() => { setForm(null); charger(); onChange?.(); }} />}
        <div className="space-y-4">
          {etageres.length === 0 && !form && <p className="text-sm text-muted-foreground">Aucune étagère pour l'instant.</p>}
          {etageres.map((et) => (
            <div key={et.id} className="rounded-xl border border-border p-3 flex flex-wrap items-start justify-between gap-3">
              <EtagereVisuel lettre={et.lettre} lignes={et.lignes} colonnes={et.colonnes} emplacements={et.emplacements} compact />
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setForm({ etagere: et })} title="Modifier"><Pencil size={14} /></Button>
                {confirmDel === et.id
                  ? <Button size="sm" variant="destructive" onClick={() => supprimer(et)}>Supprimer ?</Button>
                  : <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirmDel(et.id)} title="Supprimer"><Trash2 size={14} /></Button>}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
